import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import InteractionHints from '../InteractionHints';

const PreviewGallery = ({
  galleryImages = [],
  galleryIndex,
  setGalleryIndex,
  configRows = [],
  priceInfo,
  pipingCost = 0,
  showZipLabel = false,
  children,
}) => {
  const totalSlides = 1 + galleryImages.length;

  return (
    <div className="bg-white rounded-2xl p-4 sm:p-6 lg:p-8 shadow-card h-fit order-1 lg:order-1" style={{ position: 'inherit' }}>
      <div className="flex items-center justify-between mb-1 sm:mb-4 gap-3">
        <h3 className="text-lg font-semibold text-[#1A1A1A]">
          {galleryIndex === 0 ? '3D Live Preview' : 'Gallery'}
        </h3>
        {showZipLabel && galleryIndex === 0 && (
          <div
            className="sm:hidden flex items-center gap-2 px-3 py-1 rounded-full text-[11px] text-gray-600 shrink-0"
            style={{ background: 'linear-gradient(135deg, #f5f0eb 0%, #e8e0d8 100%)' }}
            data-testid="zip-legend-mobile"
          >
            <svg width="22" height="6" viewBox="0 0 24 6" className="flex-shrink-0">
              <line x1="0" y1="3" x2="24" y2="3" stroke="#fff" strokeWidth="2" strokeDasharray="3,2" />
            </svg>
            <span className="font-medium">Zip placement</span>
          </div>
        )}
      </div>

      <div className="relative" data-testid="preview-gallery">
        <div className="overflow-hidden rounded-xl relative h-[280px] sm:h-[480px]">
          <div className="" style={{
            position: 'absolute', inset: 0,
            opacity: galleryIndex === 0 ? 1 : 0,
            pointerEvents: galleryIndex === 0 ? 'auto' : 'none',
            transition: 'opacity 0.4s ease'
          }}>
            {children}
          </div>

          {galleryImages.map((img, idx) => (
            <div
              key={idx}
              style={{
                position: 'absolute', inset: 0,
                opacity: galleryIndex === idx + 1 ? 1 : 0,
                pointerEvents: galleryIndex === idx + 1 ? 'auto' : 'none',
                transition: 'opacity 0.4s ease',
                background: 'linear-gradient(135deg, #f5f0eb 0%, #e8e0d8 100%)'
              }}
            >
              <img src={img.src} alt={img.alt} className="w-full h-full object-cover rounded-xl" loading="lazy" data-testid={`gallery-image-${idx + 1}`} />
            </div>
          ))}
        </div>

        {/* Hidden overlay arrows (kept for reference, replaced by inline) */}
        <button onClick={() => setGalleryIndex((prev) => (prev - 1 + totalSlides) % totalSlides)} className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/50 backdrop-blur-sm rounded-full hidden items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-white/80 shadow-md transition-all cursor-pointer z-10" data-testid="gallery-prev-btn">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button onClick={() => setGalleryIndex((prev) => (prev + 1) % totalSlides)} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/50 backdrop-blur-sm rounded-full hidden items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-white/80 shadow-md transition-all cursor-pointer z-10" data-testid="gallery-next-btn">
          <ChevronRight className="h-5 w-5" />
        </button>

        {/* Mobile interaction hints — between preview and dots */}
        {galleryIndex === 0 && (
          <InteractionHints className="flex sm:hidden items-center justify-center gap-1.5 bg-white/80 backdrop-blur-sm px-2 py-1.5 rounded-full text-[10px] text-gray-500 pointer-events-none whitespace-nowrap mt-2 mx-auto w-fit" />
        )}

        {/* Inline arrows + dots */}
        <div className="flex items-center justify-center gap-3 mt-3" data-testid="gallery-dots">
          <button
            onClick={() => setGalleryIndex((prev) => (prev - 1 + totalSlides) % totalSlides)}
            className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 active:bg-gray-200 transition-colors"
            data-testid="gallery-prev-btn-inline"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2">
            {Array.from({ length: totalSlides }).map((_, idx) => (
              <button
                key={idx}
                onClick={() => setGalleryIndex(idx)}
                className={`rounded-full transition-all cursor-pointer ${
                  galleryIndex === idx ? 'w-6 h-2.5 bg-bush' : 'w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400'
                }`}
                data-testid={`gallery-dot-${idx}`}
                aria-label={idx === 0 ? '3D Preview' : `Photo ${idx}`}
              />
            ))}
          </div>
          <button
            onClick={() => setGalleryIndex((prev) => (prev + 1) % totalSlides)}
            className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 active:bg-gray-200 transition-colors"
            data-testid="gallery-next-btn-inline"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Configuration summary table */}
      {configRows.length > 0 && (
        <div className="hidden sm:block mt-6 p-4 bg-sand rounded-xl">
          <p className="text-sm font-semibold text-gray-700 mb-3">Current Configuration</p>
          <table className="w-full text-sm">
            <tbody>
              {configRows.map((row, i) => (
                <tr key={i} className="border-b border-gray-200">
                  <td className="py-2 text-gray-500">{row.label}</td>
                  <td className="py-2 text-right font-medium text-[#1A1A1A]">{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Price preview */}
      {priceInfo && (
        <div className="hidden sm:block mt-4 p-4 bg-bush/5 rounded-xl border border-bush/20">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Estimated Price</span>
            <span className="text-2xl font-bold text-bush" data-testid="price-preview">${(priceInfo.unit_price + pipingCost).toFixed(0)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreviewGallery;
